import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, SectionHeading, LeafSprig } from "@/components/Motion";
import { Packages } from "@/components/Packages";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CONTACT, IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const CateringHero = () => (
  <section className="relative flex min-h-[92svh] items-end overflow-hidden bg-noir" data-testid="catering-hero">
    <img
      src="/images/package-luxury.jpg"
      alt="Pan-seared fish with asparagus, radish and dill — plated-style presentation from Root2Shoot's Luxury package"
      className="absolute inset-0 h-full w-full object-cover"
      data-testid="catering-hero-image"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/50 to-noir/25" />
    <div className="absolute inset-0 bg-gradient-to-r from-noir/60 via-transparent to-transparent" />
    <LeafSprig className="pointer-events-none absolute -right-8 top-24 z-10 h-72 rotate-[160deg] text-cream/10" />
    <motion.img
      src="/images/logo-filmtv-banner.png"
      alt="Root2Shoot Sustainable Film and TV Catering"
      className="absolute right-6 top-24 z-10 hidden h-14 w-auto sm:block lg:right-12 lg:top-28 lg:h-16"
      animate={{ y: [0, -10, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-44 sm:px-8">
      <motion.p
        className="label-mono flex items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8 }}
      >
        Sustainable Film &amp; TV Catering
        <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
      </motion.p>
      <MaskedLines
        lines={["Good food fuels", "great stories"]}
        lineClassName={["text-cream", "text-gold"]}
        className="mt-8 font-editorial text-[12vw] leading-[0.98] tracking-tight sm:text-7xl lg:text-8xl"
      />
      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
        <motion.p
          className="max-w-xl text-base leading-relaxed text-cream/85 sm:text-lg lg:col-span-7"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.9, ease: EASE }}
        >
          Root2Shoot is an external catering service for film and TV — everything is prepared
          in our permanent kitchen, individually boxed, hotboxed and delivered hot to your set.
          Casts and crews stay fed, happy and on schedule, with sustainability baked into every service.
        </motion.p>
        <motion.div
          className="flex flex-wrap gap-4 lg:col-span-5 lg:justify-end"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9, ease: EASE }}
        >
          <a href="#packages" className="btn-gold" data-testid="catering-hero-cta-packages">View Packages</a>
          <a href="#enquiry" className="btn-ghost" data-testid="catering-hero-cta-enquiry">Start an Enquiry</a>
        </motion.div>
      </div>
    </div>
  </section>
);

const DIFFERENCE = [
  { title: "Built around call sheets", body: "5am breakfasts, night-shoot suppers, split days. We flex to your schedule, not the other way round.", testid: "diff-schedule" },
  { title: "Every diet, one table", body: "Vegan, gluten-free, halal, allergies — full coverage without the sad 'special plate'.", testid: "diff-dietary" },
  { title: "Compostable serviceware", body: "Compostable boxes, cups and cutlery come as standard on every job — no single-use plastic in our kit.", testid: "diff-plastic" },
  { title: "Every package, hotboxed", body: "We bring the food, not the kitchen — insulated hotboxes, fewer vehicles, no generator, lower cost to you.", testid: "diff-mobile" },
];

const Difference = () => (
  <section className="bg-obsidian py-24 sm:py-32" data-testid="catering-difference-section">
    <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <SectionHeading
            kicker="What We Do & Why"
            title={<>The Root2Shoot <span className="text-gold">difference</span></>}
          />
          <Reveal delay={0.1}>
            <p className="mt-7 text-base leading-relaxed text-sage">
              Productions run on trust and timing. We started Root2Shoot because crews deserve food
              that's as professional as they are — and because catering shouldn't cost the earth.
              Literally. Production work helps keep our public café, and the community around it, going.
            </p>
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        {DIFFERENCE.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.06}>
            <div className="grid gap-4 border-t border-gold/20 py-9 sm:grid-cols-[72px_1fr] sm:gap-8" data-testid={f.testid}>
              <span className="font-editorial text-4xl leading-none text-gold sm:text-5xl">0{i + 1}</span>
              <div>
                <h3 className="font-serif text-2xl tracking-tight text-cream sm:text-3xl">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage sm:text-base">{f.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Hotbox = () => (
  <section className="relative overflow-hidden bg-noir py-24 sm:py-32" data-testid="hotbox-section">
    <LeafSprig className="pointer-events-none absolute -left-8 bottom-10 h-72 -rotate-12 text-cream/10" />
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="relative lg:col-span-7">
        <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
        <div className="relative">
          <img
            src={IMAGES.hotboxChicken}
            alt="Root2Shoot hotbox with grilled chicken, asparagus and pickled onions ready for delivery"
            className="aspect-[4/3] w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid="hotbox-image"
          />
          <span
            className="absolute left-6 top-6 bg-gold px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-noir"
            data-testid="hotbox-badge"
          >
            Every Package, Hotboxed
          </span>
        </div>
      </Reveal>
      <div className="lg:col-span-5">
        <SectionHeading
          kicker="A Lighter Way to Cater"
          title={<>We bring the food, <span className="text-gold">not the kitchen</span></>}
          className="mb-7"
        />
        <Reveal delay={0.1}>
          <p className="text-base leading-relaxed text-sage sm:text-lg">
            Every Root2Shoot package — Essential, Enhanced or Luxury — is hotboxed. We prepare
            everything in our permanent kitchen, pack it into individual boxes, load the hotboxes
            and deliver it ready to serve. You get the same food without a kitchen truck ever
            rolling onto your location.
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-9 flex flex-wrap items-center gap-x-4 gap-y-3 border-y border-gold/25 py-6" data-testid="hotbox-pipeline">
            {["Permanent Kitchen", "Individual Boxes", "Hotboxes", "Delivered Hot"].map((step, i, arr) => (
              <span key={step} className="flex items-center gap-4">
                <span className="font-mono text-[11px] tracking-[0.2em] text-gold">0{i + 1}</span>
                <span className="font-serif text-lg text-cream sm:text-xl">{step}</span>
                {i < arr.length - 1 && <span className="font-display text-xl italic text-gold/50" aria-hidden="true">→</span>}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.22}>
          <ul className="mt-4" data-testid="hotbox-benefits">
            {[
              "Costs the production less — no truck, crew setup or site infrastructure",
              "Lighter on location — fewer vehicles, no generator, less water and less waste on set",
              "Prepared fresh at our permanent kitchen and timed to your breaks — arrives hot, serves fast",
              "Compostable serviceware, with suitable surplus returned safely to base, not binned",
            ].map((text, i) => (
              <li key={text} className="flex items-baseline gap-5 border-t border-gold/15 py-4 text-sm leading-relaxed text-cream/85 sm:text-base">
                <span className="font-mono text-[11px] tracking-[0.2em] text-gold">0{i + 1}</span>
                {text}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.25}>
          <a href="#enquiry" className="btn-gold mt-10" data-testid="hotbox-cta">
            Ask About Hotbox Delivery
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const SUS_ROWS = [
  { title: "Plant-forward by default", body: "Menus lead with plants; meat is local, high-welfare and never wasted.", testid: "sus-plant" },
  { title: "Closed-loop kitchens", body: "Compostable packaging, surplus to food-rescue partners, scraps to compost that returns to our farms.", testid: "sus-loop" },
  { title: "Measured, not guessed", body: "We keep an eye on our waste, packaging and miles job by job — and we're honest about what the numbers say.", testid: "sus-measured" },
  { title: "Delivered lighter", body: "Electric, hybrid or biofuel vehicles where the route allows, with journeys planned to keep miles down.", testid: "sus-delivery" },
  { title: "People first", body: "Production work supports our public café and the community around it.", testid: "sus-people" },
];

const SustainabilityStrip = () => (
  <section className="bg-obsidian py-24 sm:py-32" data-testid="sustainability-section">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <SectionHeading
          kicker="Our Sustainability Approach"
          title={<>Low-impact, <span className="text-gold">high-flavour</span></>}
          className="mb-10"
        />
        <div>
          {SUS_ROWS.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06}>
              <div className="grid gap-2 border-t border-gold/15 py-6 sm:grid-cols-[56px_1fr] sm:items-baseline sm:gap-6" data-testid={item.testid}>
                <span className="font-mono text-[11px] tracking-[0.2em] text-gold">0{i + 1}</span>
                <div>
                  <h3 className="font-serif text-xl tracking-tight text-cream">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-sage">{item.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <p className="mt-9 text-sm text-sage">
            Want the full picture?{" "}
            <Link to="/sustainability" className="text-gold underline-offset-4 hover:underline" data-testid="catering-sustainability-link">
              Read how we operate on our Sustainability page
            </Link>
            .
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.12} className="relative lg:col-span-5 lg:col-start-8">
        <div className="absolute -inset-0 -translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
        <img
          src={IMAGES.prepBowls}
          alt="Wooden prep bowls of pickled onions, shredded leeks and fresh herbs in the Root2Shoot kitchen"
          className="relative aspect-[4/5] w-full object-cover"
          loading="lazy"
          decoding="async"
          data-testid="sustainability-image"
        />
      </Reveal>
    </div>
  </section>
);

const Enquiry = ({ presetTier }) => (
  <section id="enquiry" className="scroll-mt-28 bg-noir py-24 sm:py-32" data-testid="catering-enquiry-section">
    <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading
          kicker="Let's Talk"
          title={<>Tell us about <span className="text-gold">your production</span></>}
        />
        <Reveal delay={0.1}>
          <p className="mt-7 text-base leading-relaxed text-sage sm:text-lg">
            Share a few details and we'll come back with a tailored menu and quote — usually
            within one working day.
          </p>
          <p className="mt-8 border-t border-gold/20 pt-7 text-sm text-sage">
            Prefer email?{" "}
            <a href="mailto:info@root2shoot.com" className="text-gold underline-offset-4 hover:underline" data-testid="catering-email-link">
              info@root2shoot.com
            </a>
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.12} className="lg:col-span-7">
        <div className="border border-gold/25 bg-obsidian/70 p-7 sm:p-12">
          <p className="label-mono mb-10">Catering Enquiry</p>
          <EnquiryForm variant="catering" presetTier={presetTier} />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Catering() {
  const [presetTier, setPresetTier] = useState(null);
  return (
    <>
      <Seo
        title="Sustainable Film & TV Production Catering — Hotbox Crew Catering | Root2Shoot"
        siteName="Root2Shoot"
        description="Plant-forward film and TV production catering, prepared fresh in our permanent kitchen and delivered hot in hotboxes — no kitchen truck, no generator. Crew and location catering for productions across the UK, with Essential, Enhanced and Luxury packages."
        image="/images/logo-filmtv-banner.png"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Film & TV Catering", path: "/catering" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Sustainable film & TV production catering",
          provider: {
            "@type": "FoodEstablishment",
            name: "Root2Shoot",
            email: CONTACT.email,
            telephone: CONTACT.phoneInternational,
            address: {
              "@type": "PostalAddress",
              streetAddress: CONTACT.street,
              addressLocality: CONTACT.locality,
              addressRegion: CONTACT.region,
              postalCode: CONTACT.postcode,
              addressCountry: "GB",
            },
          },
          areaServed: "United Kingdom",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Hotbox catering packages",
            itemListElement: [
              { "@type": "Offer", name: "Essential" },
              { "@type": "Offer", name: "Enhanced" },
              { "@type": "Offer", name: "Luxury" },
            ],
          },
        }}
      />
      <CateringHero />
      <Difference />
      <Hotbox />
      <Packages onEnquire={setPresetTier} enquiryHref="#enquiry" />
      <SustainabilityStrip />
      <Enquiry presetTier={presetTier} />
    </>
  );
}
